import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { useAppDispatch } from '../../../hooks/redux';
import { setWheelSelected } from '../../../store/wheel.slice';
import { IJournalResult } from '../../../store/types';

import { WheelPanel } from './wheelPanel';
import {useFormFactor} from '../../../utils/formFactor';

export interface ILifeBalanceWheelPsy {
  result?: IJournalResult;
  setResult?: React.Dispatch<React.SetStateAction<IJournalResult>>;
  sendWheelChange?: (values: number[]) => void;
  sendWheelResult?: (values: number[]) => void;
  wheelBootstrap?: { nonce: number; values: number[] } | null;
  onWheelBootstrapConsumed?: () => void;
}

export const LifeBalanceWheelClient: FC<ILifeBalanceWheelPsy> = ({
  sendWheelChange,
  sendWheelResult,
  wheelBootstrap,
  onWheelBootstrapConsumed,
}) => {
  const [active, setActive] = useState(true);
  const [wheelValues, setWheelValues] = useState<number[]>([1, 2, 3, 4, 5, 6, 7, 8]);
  const lastSentAtRef = useRef(0);
  const pendingValuesRef = useRef<number[] | null>(null);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  //  const wh = useAppSelector((state) => state.wheelStateSlice.selectedValues);
  const dispatch = useAppDispatch();
  const formFactor = useFormFactor();
  const EMIT_THROTTLE_MS = 80;

  const areValuesEqual = (a: number[], b: number[]) => a.length === b.length && a.every((v, i) => v === b[i]);

  const sendAction = useCallback(
    (action: string, values: number[]) => {
      if (action === 'change_wheel') {
        sendWheelChange?.(values);
      } else if (action === 'result_wheel') {
        sendWheelResult?.(values);
      }
    },
    [sendWheelChange, sendWheelResult],
  );

  const flushPending = useCallback(() => {
    if (!pendingValuesRef.current) return;
    sendAction('change_wheel', pendingValuesRef.current);
    lastSentAtRef.current = Date.now();
    pendingValuesRef.current = null;
    flushTimerRef.current = null;
  }, [sendAction]);

  useEffect(() => {
    return () => {
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!wheelBootstrap) return;
    setWheelValues(wheelBootstrap.values);
    dispatch(setWheelSelected(wheelBootstrap.values));
    onWheelBootstrapConsumed?.();
  }, [wheelBootstrap, dispatch, onWheelBootstrapConsumed]);

  const onChange = useCallback(
    (values: number[]) => {
      if (areValuesEqual(values, wheelValues)) return;
      // Обновляем локально до отправки на сервер, чтобы клиент видел результат сразу
      setWheelValues(values);
      const now = Date.now();
      const diff = now - lastSentAtRef.current;
      if (diff >= EMIT_THROTTLE_MS) {
        sendAction('change_wheel', values);
        lastSentAtRef.current = now;
        pendingValuesRef.current = null;
        if (flushTimerRef.current) {
          clearTimeout(flushTimerRef.current);
          flushTimerRef.current = null;
        }
        return;
      }

      pendingValuesRef.current = values;
      if (!flushTimerRef.current) {
        flushTimerRef.current = setTimeout(flushPending, EMIT_THROTTLE_MS - diff);
      }
    },
    [wheelValues, sendAction, flushPending],
  );

  const onReady = () => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    if (pendingValuesRef.current) {
      sendAction('change_wheel', pendingValuesRef.current);
      pendingValuesRef.current = null;
    }
    setActive(false);
    sendAction('result_wheel', wheelValues);
    dispatch(setWheelSelected(wheelValues));
  };

  const wheelPanel = active ? (
    <WheelPanel values={wheelValues} isClient setValues={onChange} setReady={onReady} />
  ) : (
    <WheelPanel values={wheelValues} isClient />
  );

  if (formFactor === 'mobile-portrait') {
    return <div className="w-full h-full flex flex-row gap-[23px] relative pt-[8rem]">{wheelPanel}</div>;
  }
  if (formFactor === 'mobile-landscape') {
    return <div className="w-full h-full flex flex-row gap-[23px] relative">{wheelPanel}</div>;
  }
  return <div className="w-full h-full flex flex-row gap-[23px] relative">{wheelPanel}</div>;

  
};
